import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, CheckCircle2, ArrowRight } from 'lucide-react';
import { sendFormEmail } from '../utils/sendFormEmail';
import { categories } from '../data/products';
export function Contact() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const form = e.target as HTMLFormElement;
      const materialSelect = form.querySelector('select[class*="appearance-none"]') as HTMLSelectElement;
      const yardageSelect = form.querySelectorAll('select[class*="appearance-none"]')[1] as HTMLSelectElement;
      const addressInput = form.querySelector('input[placeholder="123 Main St, Tacoma, WA"]') as HTMLInputElement;
      await sendFormEmail({
        subject: '253Gravel — New Contact / Quote Request',
        'First Name': formData.firstName,
        'Last Name': formData.lastName,
        email: formData.email,
        _replyto: formData.email,
        _autoresponse: `Hi ${formData.firstName}! Thank you for reaching out to 253 Gravel! We've received your message and will get back to you shortly.\n\nIf you need anything sooner, call us at (206) 619-3742.\n\n- The 253 Gravel Team`,
        Phone: formData.phone,
        Material: materialSelect?.value || '',
        'Estimated Yardage': yardageSelect?.value || '',
        'Delivery Address': addressInput?.value || '',
        Message: formData.message
      });
      setIsSubmitted(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: ''
      });
      setTimeout(() => {
        formRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const inputClass = 'w-full bg-[#f9f8f6] dark:bg-[#454342] border border-stone-300 dark:border-[#575555] rounded-xl px-4 py-3 text-sm text-brand-charcoal dark:text-white placeholder-brand-muted dark:placeholder-stone-600 focus:ring-2 focus:ring-brand-orange focus:border-transparent outline-none transition-all';
  return <main className="min-h-[calc(100vh-64px)] bg-[#faf9f7] dark:bg-[#454342] py-12 px-4 sm:px-6 lg:px-8 transition-colors duration-300 flex items-center justify-center" data-id="element-1422">
      <div className="max-w-6xl w-full mx-auto" data-id="element-1423">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="mb-10 text-center lg:text-left" data-id="element-1424">
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-stone-900 dark:text-white mb-3 tracking-tight" data-id="element-1425">
            Contact & <span className="text-brand-orange" data-id="element-1426">Quotes</span>
          </h1>
          <p className="text-stone-600 dark:text-stone-300 text-sm md:text-base max-w-2xl mx-auto lg:mx-0 font-sub" data-id="element-1427">
            Need a quote or have a question? Reach out directly or drop your
            project details below.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1428">
          {/* Left Column: Form */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} ref={formRef} className="lg:col-span-2 bg-[#fcfbfa] dark:bg-[#535252] rounded-2xl p-6 md:p-8 border border-stone-200 dark:border-[#636262] shadow-lg" data-id="element-1429">
            {isSubmitted ? <div className="h-full flex flex-col items-center justify-center text-center py-12" data-id="element-1430">
                <div className="w-16 h-16 bg-brand-orange/10 rounded-full flex items-center justify-center mb-4" data-id="element-1431">
                  <CheckCircle2 className="w-8 h-8 text-brand-orange" data-id="element-1432" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-stone-900 dark:text-white mb-2" data-id="element-1433">
                  Request Received
                </h3>
                <p className="text-stone-600 dark:text-stone-300 mb-6 max-w-sm text-sm" data-id="element-1434">
                  Thanks for reaching out. We'll get back to you shortly with
                  pricing and availability.
                </p>
                <button onClick={() => setIsSubmitted(false)} className="px-6 py-2.5 bg-brand-orange hover:bg-brand-orangeHover text-white rounded-none font-bold text-sm transition-all" data-id="element-1435">
                  Send Another Request
                </button>
              </div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1436">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1437">
                  <div data-id="element-1438">
                    <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-1439">
                      First Name *
                    </label>
                    <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required className={inputClass} data-id="element-1440" />
                  </div>
                  <div data-id="element-1441">
                    <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-1442">
                      Last Name *
                    </label>
                    <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required className={inputClass} data-id="element-1443" />
                  </div>
                </div>

                <div data-id="element-1444">
                  <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-1445">
                    Email Address *
                  </label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required className={inputClass} data-id="element-1446" />
                </div>

                <div data-id="element-1447">
                  <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-1448">
                    Phone Number
                  </label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className={inputClass} data-id="element-1449" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1450">
                  <div data-id="element-1451">
                    <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-1452">
                      Material Needed
                    </label>
                    <select className={inputClass + ' appearance-none'} data-id="element-1453">
                      <option value="" data-id="element-1454">Select a material...</option>
                      {categories.map(cat => <optgroup key={cat.name} label={cat.name} data-id="element-1455">
                          {cat.products.map(p => <option key={p.slug} value={p.name} data-id="element-1456">
                              {p.name} — ${p.pricePerYard}/yd
                            </option>)}
                        </optgroup>)}
                      <option value="Not Sure Yet" data-id="element-1457">Not Sure Yet</option>
                    </select>
                  </div>
                  <div data-id="element-1458">
                    <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-1459">
                      Estimated Yardage
                    </label>
                    <select className={inputClass + ' appearance-none'} data-id="element-1460">
                      <option data-id="element-1461">5 Yards (Minimum)</option>
                      <option data-id="element-1462">10 Yards</option>
                      <option data-id="element-1463">15 Yards</option>
                      <option data-id="element-1464">20+ Yards</option>
                      <option data-id="element-1465">Need help calculating</option>
                    </select>
                  </div>
                </div>

                <div data-id="element-1466">
                  <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-1467">
                    Delivery Address *
                  </label>
                  <input type="text" required className={inputClass} placeholder="123 Main St, Tacoma, WA" data-id="element-1468" />
                </div>

                <div data-id="element-1469">
                  <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-1470">
                    Message *
                  </label>
                  <textarea name="message" value={formData.message} onChange={handleChange} required rows={4} className={inputClass + ' resize-none'} data-id="element-1471"></textarea>
                </div>

                <div className="pt-2" data-id="element-1472">
                  <button type="submit" disabled={isSubmitting} className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3 bg-brand-button hover:bg-brand-buttonHover text-white rounded-none font-bold text-sm transition-all group disabled:opacity-60" data-id="element-1473">
                    {isSubmitting ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-1474" /> : <>
                        Send Quote Request
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1475" />
                      </>}
                  </button>
                </div>
              </form>}
          </motion.div>

          {/* Right Column: Info */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="space-y-6" data-id="element-1476">
            {/* Contact Info Card */}
            <div className="bg-[#fcfbfa] dark:bg-[#535252] text-stone-900 dark:text-white rounded-2xl p-6 border border-stone-200 dark:border-[#636262] shadow-lg" data-id="element-1477">
              <h3 className="font-heading text-lg font-bold mb-5" data-id="element-1478">
                Get in Touch
              </h3>
              <div className="space-y-4" data-id="element-1479">
                <a href="tel:206-619-3742" className="flex items-center group" data-id="element-1480">
                  <div className="w-10 h-10 bg-brand-orange/10 rounded-xl flex items-center justify-center mr-3 group-hover:bg-brand-orange/20 transition-colors" data-id="element-1481">
                    <Phone className="w-4 h-4 text-brand-orange" data-id="element-1482" />
                  </div>
                  <div data-id="element-1483">
                    <p className="text-xs text-stone-500 dark:text-stone-400 mb-0.5" data-id="element-1484">
                      Call Us
                    </p>
                    <p className="font-medium text-sm text-stone-900 dark:text-white" data-id="element-1485">
                      (206) 619-3742
                    </p>
                  </div>
                </a>
                <a href="mailto:info@253gravel.com" className="flex items-center group" data-id="element-1486">
                  <div className="w-10 h-10 bg-brand-orange/10 rounded-xl flex items-center justify-center mr-3 group-hover:bg-brand-orange/20 transition-colors" data-id="element-1487">
                    <Mail className="w-4 h-4 text-brand-orange" data-id="element-1488" />
                  </div>
                  <div data-id="element-1489">
                    <p className="text-xs text-stone-500 dark:text-stone-400 mb-0.5" data-id="element-1490">
                      Email Us
                    </p>
                    <p className="font-medium text-sm text-stone-900 dark:text-white" data-id="element-1491">
                      info@253gravel.com
                    </p>
                  </div>
                </a>
                <div className="flex items-center" data-id="element-1492">
                  <div className="w-10 h-10 bg-brand-orange/10 rounded-xl flex items-center justify-center mr-3" data-id="element-1493">
                    <MapPin className="w-4 h-4 text-brand-orange" data-id="element-1494" />
                  </div>
                  <div data-id="element-1495">
                    <p className="text-xs text-stone-500 dark:text-stone-400 mb-0.5" data-id="element-1496">
                      Location
                    </p>
                    <p className="font-medium text-sm text-stone-900 dark:text-white" data-id="element-1497">
                      Bonney Lake, WA
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="bg-[#fcfbfa] dark:bg-[#535252] rounded-2xl p-6 border border-stone-200 dark:border-[#636262] shadow-lg" data-id="element-1498">
              <h3 className="font-heading text-lg font-bold text-stone-900 dark:text-white mb-4 flex items-center" data-id="element-1499">
                <Clock className="w-4 h-4 mr-2 text-brand-orange" data-id="element-1500" />
                Hours
              </h3>
              <div className="space-y-3 text-sm" data-id="element-1501">
                <div className="flex justify-between items-center pb-2 border-b border-stone-200 dark:border-[#636262]" data-id="element-1502">
                  <span className="text-stone-600 dark:text-stone-300" data-id="element-1503">
                    Mon - Fri
                  </span>
                  <span className="font-medium text-stone-900 dark:text-white" data-id="element-1504">
                    6:00 AM – 5:00 PM
                  </span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-stone-200 dark:border-[#636262]" data-id="element-1505">
                  <span className="text-stone-600 dark:text-stone-300" data-id="element-1506">
                    Saturday
                  </span>
                  <span className="font-medium text-stone-900 dark:text-white" data-id="element-1507">
                    7:00 AM – 3:00 PM
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-1508">
                  <span className="text-stone-600 dark:text-stone-300" data-id="element-1509">
                    Sunday
                  </span>
                  <span className="font-medium text-brand-orange" data-id="element-1510">Closed</span>
                </div>
              </div>
            </div>

            {/* Service Area */}
            <div className="px-1 pt-2" data-id="element-1511">
              <h4 className="font-heading text-sm font-bold text-stone-900 dark:text-white mb-2" data-id="element-1512">
                Service Area
              </h4>
              <p className="text-xs text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-1513">
                Proudly serving Tacoma, Puyallup, Bonney Lake, Federal Way,
                Auburn, Kent, Lakewood, and surrounding King County communities
                with reliable, same-day and next-day delivery. Not sure if
                you're in our delivery zone? Give us a call — we're happy to
                help.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </main>;
}